import { AbstractMesh, AnimationGroup, Ray, Vector3 } from '@babylonjs/core';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { Level } from '../Level';
import { AbstractMeshBehavior } from './AbstractMeshBehavior';

/**
 * Available animation names in mini_characters GLB files
 */
export const ENEMY_ANIMATIONS = {
  IDLE: 'idle',
  WALK: 'walk',
  SPRINT: 'sprint',
  JUMP: 'jump',
  FALL: 'fall',
  CROUCH: 'crouch',
  SIT: 'sit',
  DIE: 'die',
  PICK_UP: 'pick-up',
  EMOTE_YES: 'emote-yes',
  EMOTE_NO: 'emote-no',
  ATTACK_MELEE_RIGHT: 'attack-melee-right',
  ATTACK_MELEE_LEFT: 'attack-melee-left',
  ATTACK_KICK_RIGHT: 'attack-kick-right',
  ATTACK_KICK_LEFT: 'attack-kick-left',
} as const;

/**
 * Base class for all enemy behaviors
 * Extends AbstractMeshBehavior with enemy-specific functionality and animation support
 */
export abstract class EnemyBehavior extends AbstractMeshBehavior {
  protected startPosition: Vector3;
  protected config: EnemyDistributionConfig;

  // Animation groups
  protected animations: Map<string, AnimationGroup> = new Map();
  protected currentAnim: AnimationGroup | null = null;
  protected animationsInitialized: boolean = false;

  // Moving Platform support
  protected currentPlatform: any = null;

  constructor(name: string, startPosition: Vector3, config: EnemyDistributionConfig) {
    super(name);
    this.config = config;
    this.startPosition = startPosition.clone();
  }

  initLevel(level: Level): AbstractMeshBehavior {
    super.initLevel(level);
    this.initAnimations();
    return this;
  }

  initSound() {
    if (this.config.sound) {
      this.setSound(this.config.sound);
    }
  }

  /**
   * Initialize animations from the mesh's asset
   */
  attach(mesh: AbstractMesh): void {
    super.attach(mesh);
  }

  /**
   * Set pre-cloned animations for this enemy
   * Called by EnemyGenerator after cloning mesh with animations
   */
  setAnimations(animations: AnimationGroup[]): void {
    for (const anim of animations) {
      const nameLower = anim.name.toLowerCase();

      // Map the animation to our expected animation names
      for (const [key, animName] of Object.entries(ENEMY_ANIMATIONS)) {
        if (nameLower.includes(animName)) {
          anim.stop();
          this.animations.set(animName, anim);
          break;
        }
      }
    }

    this.animationsInitialized = true;

    // Start with idle animation if available
    this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
  }

  /**
   * Initialize animations for this enemy
   * Finds animation groups that target this mesh's skeleton
   */
  protected initAnimations(): void {
    if (this.animationsInitialized || !this.level) {
      return;
    }

    const scene = this.level.scene;

    // Find animations for this mesh by looking for groups that match enemy animation names
    for (const [key, animName] of Object.entries(ENEMY_ANIMATIONS)) {
      const group = this.level.scene.getAnimationGroupByName(animName);
      if (group) {
        group.stop();
        // Clone the animation for this specific enemy
        const clonedAnim = group.clone(`${this.mesh.name}_${animName}`, (oldTarget) => {
          // Retarget to this mesh's skeleton if possible
          return oldTarget;
        });

        if (clonedAnim) {
          clonedAnim.stop();
          this.animations.set(animName, clonedAnim);
        }
      }
    }

    this.animationsInitialized = true;

    // Start with idle animation if available
    this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
  }

  /**
   * Play an animation by name
   */
  protected playAnimation(name: string, loop: boolean = true, speed: number = 1): void {
    const anim = this.animations.get(name);

    if (!anim) {
      return;
    }

    // Don't restart if already playing this animation
    if (this.currentAnim === anim && anim.isPlaying) {
      return;
    }

    // Stop current animation
    if (this.currentAnim && this.currentAnim !== anim) {
      this.currentAnim.stop();
    }

    // Play new animation
    anim.speedRatio = speed;
    anim.loopAnimation = loop;
    anim.play(loop);
    this.currentAnim = anim;
  }

  /**
   * Stop all animations
   */
  protected stopAllAnimations(): void {
    for (const anim of this.animations.values()) {
      anim.stop();
    }
    this.currentAnim = null;
  }

  /**
   * Update the behavior - called every frame
   */
  update(deltaTime: number): void {
    // Apply platform movement BEFORE behavior logic
    if (this.currentPlatform) {
      if (this.currentPlatform.velocity) {
        const move = this.currentPlatform.velocity.scale(deltaTime);
        this.mesh.position.addInPlace(move);
        this.startPosition.addInPlace(move);
      }
    }

    this.behaviorUpdate(deltaTime);
  }

  protected abstract behaviorUpdate(deltaTime: number): void;

  /**
   * Get distance to player
   */
  protected getPlayerDistance(): number {
    if (!this.level?.player) {
      return Infinity;
    }
    return this.level.player.getDistance(this.mesh.position);
  }

  /**
   * Get player position
   */
  protected getPlayerPosition(): Vector3 {
    return this.level?.player?.getPosition() ?? Vector3.Zero();
  }

  /**
   * Face toward a target position (Y axis rotation only)
   */
  protected lookAt(target: Vector3): void {
    const direction = target.subtract(this.mesh.position);
    direction.y = 0; // Keep on horizontal plane
    if (direction.length() > 0.01) {
      const angle = Math.atan2(direction.x, direction.z);
      this.mesh.rotation.y = angle;
    }
  }

  /**
   * Move toward a position
   */
  protected moveToward(target: Vector3, deltaTime: number, lookAtTarget: boolean = true): void {
    const direction = target.subtract(this.mesh.position);
    direction.y = 0; // Keep on horizontal plane

    if (direction.length() > 0.1) {
      const normalizedDir = direction.normalize();
      const movement = normalizedDir.scale(this.config.speed * deltaTime);

      this.mesh.position.addInPlace(movement);

      if (lookAtTarget) {
        this.lookAt(target);
      }

      this.snapToGround(deltaTime);
    }
  }

  /**
   * Raycast down to find ground and snap mesh Y position to it.
   */
  /**
   * Raycast down to find ground and snap mesh Y position to it.
   * Also handles moving platforms by applying their velocity.
   */
  protected snapToGround(deltaTime: number = 0): void {
    if (!this.level || !this.mesh) return;

    // Start ray above the TOP of the enemy to avoid starting inside the mesh
    // We start high up (50 units) to catch platforms even if we spawned below them
    const boundingInfo = this.mesh.getHierarchyBoundingVectors();
    const rayOrigin = this.mesh.position.clone();
    rayOrigin.y = Math.max(rayOrigin.y + 50.0, boundingInfo.max.y + 50.0);

    // Cast down 100 units
    const ray = new Ray(rayOrigin, Vector3.Down(), 100.0);

    // Pick against collision meshes (ground, platforms)
    const pick = this.level.scene.pickWithRay(ray, (mesh) => {
      return mesh.metadata?.ground || mesh.metadata?.way || mesh.metadata?.platform;
    });

    // Only snap if we hit something AND it's a floor (upward facing)
    const normal = pick.getNormal(true);
    if (pick && pick.hit && pick.pickedPoint && normal && normal.y > 0.4) {
      // Platform logic
      if (pick.pickedMesh && pick.pickedMesh.metadata && pick.pickedMesh.metadata.movingPlatform) {
        this.currentPlatform = pick.pickedMesh.metadata.movingPlatform;
      } else {
        this.currentPlatform = null;
      }

      this.mesh.position.y = pick.pickedPoint.y;
    } else {
      this.currentPlatform = null;
    }
  }

  /**
   * Get the height of the ground at current X,Z position
   */
  protected getGroundHeight(): number | null {
    if (!this.level || !this.mesh) return null;

    // Start ray above the TOP of the enemy to avoid starting inside the mesh
    // We start high up (50 units) to catch platforms even if we spawned below them
    const boundingInfo = this.mesh.getHierarchyBoundingVectors();
    const rayOrigin = this.mesh.position.clone();
    rayOrigin.y = Math.max(rayOrigin.y + 50.0, boundingInfo.max.y + 50.0);

    // Cast down long enough to find ground even if high up
    const ray = new Ray(rayOrigin, Vector3.Down(), 100.0);

    // Pick against collision meshes (ground, platforms)
    const pick = this.level.scene.pickWithRay(ray, (mesh) => {
      return mesh.metadata?.ground || mesh.metadata?.way || mesh.metadata?.platform;
    });

    const normal = pick.getNormal(true);
    if (pick && pick.hit && pick.pickedPoint && normal && normal.y > 0.4) {
      return pick.pickedPoint.y;
    }
    return null;
  }

  /**
   * Dispose animations when behavior is detached
   */
  detach(): void {
    this.stopAllAnimations();
    for (const anim of this.animations.values()) {
      anim.dispose();
    }
    this.animations.clear();
    super.detach();
  }
}
